import { apiClient } from './client'

// VoIP Types
export interface VoipExtensionState {
  extension: string
  technology: string
  endpoint_state: 'online' | 'offline' | 'unknown'
  status: 'registered' | 'in_call' | 'ringing' | 'unregistered' | 'unknown'
  is_registered: boolean
  is_in_call: boolean
  active_calls: VoipActiveCall[]
  operator_id: number | null
  operator_name: string | null
  last_updated: string
}

export interface VoipActiveCall {
  channel_id: string
  state: string
  caller: { name: string; number: string }
  connected: { name: string; number: string }
  direction: string
  started_at: string | null
  duration_seconds: number | null
}

export interface VoipExtensionListResponse {
  total: number
  registered: number
  in_call: number
  extensions: VoipExtensionState[]
}

export interface VoipAsteriskInfo {
  status: 'online' | 'offline' | 'error'
  version: string | null
  uptime_seconds: number | null
  active_channels: number
  active_calls: number
  total_endpoints: number
  registered_endpoints: number
  last_check: string
  error_message: string | null
}

export interface VoipInfrastructure {
  asterisk: VoipAsteriskInfo
  last_updated: string
}

export interface VoipSummary {
  asterisk: {
    status: string
    version: string | null
    active_calls: number
  }
  extensions: {
    total: number
    registered: number
    in_call: number
    unregistered: number
  }
  last_updated: string
}

export interface VoipHealthCheck {
  healthy: boolean
  status: string
  message: string
  checked_at: string
}

// Call History Types
export interface CallEvent {
  time: string
  extension: string
  event_type: 'call_start' | 'call_end'
  status: string | null
  caller_number: string | null
  caller_name: string | null
  connected_number: string | null
  connected_name: string | null
  direction: 'inbound' | 'outbound' | null
  duration_seconds: number | null
  operator_id: number | null
}

export interface CallHistoryResponse {
  total: number
  events: CallEvent[]
}

export interface CallPeriod {
  start_time: string
  end_time: string | null
  duration_seconds: number | null
  direction: 'inbound' | 'outbound' | null
  connected: string | null
  is_active: boolean
}

export interface CallTimelineResponse {
  extension: string
  operator_id: number | null
  operator_name: string | null
  periods: CallPeriod[]
  total_calls: number
  total_duration_seconds: number
}

// API Functions
export const voipApi = {
  /**
   * Get VoIP infrastructure status
   */
  getInfrastructure: async (): Promise<VoipInfrastructure> => {
    const response = await apiClient.get('/voip/infrastructure')
    return response.data
  },

  /**
   * Get Asterisk server status
   */
  getAsteriskStatus: async (): Promise<VoipAsteriskInfo> => {
    const response = await apiClient.get('/voip/asterisk')
    return response.data
  },

  /**
   * List all extensions
   */
  getExtensions: async (onlyMapped = false): Promise<VoipExtensionListResponse> => {
    const response = await apiClient.get('/voip/extensions', {
      params: { only_mapped: onlyMapped },
    })
    return response.data
  },

  /**
   * Get single extension state
   */
  getExtension: async (extension: string): Promise<VoipExtensionState> => {
    const response = await apiClient.get(`/voip/extensions/${extension}`)
    return response.data
  },

  /**
   * Get VoIP summary for dashboard
   */
  getSummary: async (): Promise<VoipSummary> => {
    const response = await apiClient.get('/voip/summary')
    return response.data
  },

  /**
   * Check VoIP health
   */
  healthCheck: async (): Promise<VoipHealthCheck> => {
    const response = await apiClient.get('/voip/health')
    return response.data
  },

  /**
   * Get VoIP status for an operator
   */
  getOperatorVoipStatus: async (operatorId: number) => {
    const response = await apiClient.get(`/voip/operators/${operatorId}`)
    return response.data
  },

  /**
   * Get call history for an extension
   */
  getCallHistory: async (extension: string, hours = 24, limit = 100): Promise<CallHistoryResponse> => {
    const response = await apiClient.get(`/voip/history/${extension}`, {
      params: { hours, limit },
    })
    return response.data
  },

  /**
   * Get call timeline for an extension (for charts)
   */
  getCallTimeline: async (extension: string, hours = 8): Promise<CallTimelineResponse> => {
    const response = await apiClient.get(`/voip/timeline/${extension}`, {
      params: { hours },
    })
    return response.data
  },
}

export default voipApi
